import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '@/services/api';
import { toast } from 'sonner';
import { Bell, Star, Gift, Clock, Check, ChevronRight, CheckCheck } from 'lucide-react';

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const data = await getNotifications(50);
      setNotifications(data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_review':
        return <Star className="w-5 h-5 text-[#D97757]" />;
      case 'referral_bonus':
        return <Gift className="w-5 h-5 text-[#5F8D4E]" />;
      case 'subscription_reminder':
        return <Clock className="w-5 h-5 text-amber-500" />;
      default:
        return <Bell className="w-5 h-5 text-stone-500" />;
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="notifications-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-stone-900">Notifications</h1>
            <p className="text-stone-600 mt-1">
              {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button 
              variant="outline" 
              onClick={handleMarkAllRead}
              className="h-10 rounded-full border-stone-200"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Mark All Read
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <Card className="border-stone-100 shadow-sm" data-testid="notifications-card">
          <CardContent className="p-0">
            {loading ? (
              <div className="py-12 text-center text-stone-500">Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No notifications yet</p>
                <p className="text-sm text-stone-400 mt-2">
                  You'll get notified when someone reviews a client you've worked with
                </p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {notifications.map((notification) => (
                  <div 
                    key={notification.id}
                    className={`p-4 sm:p-5 flex items-start gap-4 transition-colors ${
                      notification.is_read ? 'bg-white' : 'bg-[#D97757]/5'
                    }`}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      notification.is_read ? 'bg-stone-100' : 'bg-white shadow-sm'
                    }`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className={`font-medium ${notification.is_read ? 'text-stone-700' : 'text-stone-900'}`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-stone-600 mt-0.5">{notification.message}</p>
                        </div>
                        <span className="text-xs text-stone-500 flex-shrink-0">
                          {formatDate(notification.created_at)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3 mt-3">
                        {notification.client_id && (
                          <Link 
                            to={`/clients/${notification.client_id}`}
                            className="text-sm text-[#D97757] hover:underline flex items-center"
                          >
                            View Client <ChevronRight className="w-4 h-4 ml-1" />
                          </Link>
                        )}
                        {!notification.is_read && (
                          <button
                            onClick={() => handleMarkRead(notification.id)}
                            className="text-sm text-stone-500 hover:text-stone-700 flex items-center"
                            data-testid={`mark-read-${notification.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Mark as read
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
